"use client";

import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cx } from "@/components/ui/cx";
import { authJourneyStyles as styles } from "./auth-journey";

export interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string;
  labelAside?: ReactNode;
  error?: string | null;
  strength?: {
    value: number;
    max: number;
    label: string;
  };
  inputClassName?: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField(
    {
      label,
      labelAside,
      error,
      strength,
      id: providedId,
      inputClassName,
      "aria-describedby": describedBy,
      ...inputProps
    },
    ref,
  ) {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const errorId = `${id}-error`;
    const strengthId = `${id}-strength`;
    const [visible, setVisible] = useState(false);
    const descriptions = [
      describedBy,
      error ? errorId : null,
      strength ? strengthId : null,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="tdo-field">
        {labelAside ? (
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: "var(--tdo-sp-3)",
            }}
          >
            <label className="tdo-label" htmlFor={id}>
              {label}
            </label>
            {labelAside}
          </div>
        ) : (
          <label className="tdo-label" htmlFor={id}>
            {label}
          </label>
        )}
        <div className={styles.passwordWrap}>
          <input
            {...inputProps}
            ref={ref}
            className={cx("tdo-input", styles.passwordInput, inputClassName)}
            id={id}
            type={visible ? "text" : "password"}
            aria-invalid={Boolean(error)}
            aria-describedby={descriptions || undefined}
          />
          <button
            type="button"
            className={styles.passwordToggle}
            onClick={() => setVisible((current) => !current)}
            aria-controls={id}
            aria-pressed={visible}
            aria-label={`${visible ? "Hide" : "Show"} ${label.toLowerCase()}`}
          >
            {visible ? "Hide" : "Show"}
          </button>
        </div>
        {error ? (
          <div
            id={errorId}
            className="tdo-hint"
            role="alert"
            style={{ color: "var(--tdo-red)" }}
          >
            {error}
          </div>
        ) : null}
        {strength ? (
          <div className={styles.strength} id={strengthId}>
            <meter
              min={0}
              max={strength.max}
              value={strength.value}
              aria-label={`Password strength: ${strength.label}`}
            />
            <span>{strength.label}</span>
          </div>
        ) : null}
      </div>
    );
  },
);
